import { defineStore } from 'pinia';
import * as Sentry from '@sentry/vue';
import type {
  DailyCheckinBoard,
  DailyCheckinClaimErrorKind,
  DailyCheckinReward,
  DailyCheckinRewardState,
  DailyCheckinRewardView,
} from '~/types/daily-checkin';
import { DailyCheckinClaimError } from '~/adapters/api-daily-checkin/daily-checkin-api';
import {
  clearDailyCheckinCache,
  fetchDailyCheckinHandler,
} from '~/handlers/fetch-daily-checkin.handler';
import { postDailyCheckinClaimHandler } from '~/handlers/post-daily-checkin-claim.handler';
import { usePlayerProfileStore } from '~/stores/player-profile';
import { GAMIFICATION_SLUGS } from '~/types/player-profile';
import { tryCatch } from '~/utils/apostatudo/try-catch';

// O gate do check-in tem DOIS degraus, e eles respondem perguntas diferentes:
//
// 1. NÍVEL — o mínimo da gamificação no `/players/me`
//    (`gamifications.daily_check_in.min_level`). Libera VER: abaixo dele o
//    jogador não alcança a promoção, e o aviso nomeia o degrau que falta.
// 2. SEGMENTO — a rota do board decide se existe trilha PRA AQUELE jogador.
//    Passar no nível não garante board: a audiência do período é segmentada no
//    admin, e quem está fora dela não tem o que abrir. Aqui não há degrau pra
//    prometer — a resposta é "não é pra você", não "falta X".
//
// 'unavailable' = não há trilha pra ESTE jogador. Cobre os dois caminhos que o
// core não distingue no payload (200 com `period_type: null` e `rewards: []`):
// nenhum período publicado, ou nenhum segmento que o inclua. É resposta
// legítima, não falha — por isso é um estado próprio e não 'error' (que oferece
// "tentar novamente", e aqui não há o que tentar).
//
// 'ineligible' = o degrau 1, o de nível. Desde o ENG-787 esta é a ÚNICA origem
// do estado — o `/players/me`, nunca um campo da rota do board. Separado do
// 'unavailable' pela COPY: aqui o aviso nomeia o degrau que falta; lá ele diz
// que não há nada pra este jogador.
//
// NENHUM dos dois tranca o card do hub. Com audiência segmentada, board vazio é
// fato POR JOGADOR: "Em breve" no card seria mentira coletiva, porque a promoção
// está rodando — só não pra ele. Ver o comentário no LoyaltyHub.vue.
export type DailyCheckinStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'unavailable'
  | 'ineligible'
  | 'error';

// Mensagem por motivo de recusa. Fica aqui, e não no adapter, pra copy não
// atravessar camada (mesmo desenho da roletinha). O core já manda `message` em
// PT-BR, mas quem decide o texto exibido é a tela: assim a copy é a nossa e não
// muda por trás quando o back reescrever a dele.
const CLAIM_MESSAGES: Record<DailyCheckinClaimErrorKind, string> = {
  unauthenticated: 'Sua sessão expirou. Entre novamente para resgatar.',
  no_active_period: 'As recompensas deste período já encerraram.',
  // O core manda a copy dele ("Faça sua primeira aposta para participar do
  // check-in."), mas quem escreve pro jogador é esta camada — e aqui dá pra ser
  // honesto no caso que a copy do core erra: o mesmo 403 sai numa queda do
  // cactus, pra quem JÁ apostou e só não tem o `true` memoizado. Mandar essa
  // pessoa "fazer a primeira aposta" é mentira; a segunda frase é pra ela.
  not_eligible:
    'Faça sua primeira aposta para participar do check-in. Se já apostou, tente de novo em instantes.',
  already_claimed_today: 'Você já resgatou hoje. Volte amanhã!',
  track_completed: 'Você já resgatou todas as recompensas deste período.',
  delivery_failed: 'Não foi possível creditar seu prêmio. Tente novamente.',
  delivery_uncertain:
    'Seu resgate está em conferência. Se o prêmio não aparecer em alguns minutos, fale com o suporte.',
  step_unavailable: 'Esta recompensa está indisponível. Fale com o suporte.',
  invalid_user: 'Não foi possível identificar sua conta.',
  rate_limited: 'Muitas tentativas. Aguarde um momento e tente novamente.',
  network: 'Não foi possível conectar. Tente novamente.',
  api: 'Erro ao resgatar sua recompensa.',
};

// Falhas esperadas do fluxo não interessam à triagem do Sentry. Sobem só as que
// pedem ação de alguém: erro de API/desconhecido, entrega incerta (conferência
// humana), user_id recusado (bug nosso, não do jogador) e degrau sem prêmio
// configurado — este último trava a trilha de TODO mundo que chegar nele, e
// ninguém descobre pelo suporte antes do alerta.
//
// `not_eligible` fica FORA: pra quem nunca apostou é o fluxo funcionando, e é o
// caso da esmagadora maioria de quem chega nele. Ele também aparece numa queda do
// cactus, que valeria alerta — mas os dois casos chegam aqui idênticos, e alertar
// nos dois afogaria o sinal no ruído de jogador comum batendo num board velho.
const REPORTED_KINDS: DailyCheckinClaimErrorKind[] = [
  'api',
  'delivery_uncertain',
  'invalid_user',
  'step_unavailable',
];

// Falhas que provam que o resgate NÃO foi commitado: o pedido morreu antes da
// regra de negócio rodar — 401 sem sessão, 422 na validação do Laravel, 429 no
// throttle. Só estas podem ser anunciadas como recusa direto.
//
// Todo o resto passa pela reconciliação, porque um erro de resgate NÃO prova que
// o resgate falhou: a recusa pode chegar depois do core já ter gravado (resposta
// perdida no caminho, 5xx na entrega, corpo que não deu pra ler). Anunciar
// fracasso em cima disso é mostrar erro vermelho no clique que creditou o
// prêmio, e foi o que o 500 do ENG-425 fez em staging.
//
// `not_eligible` CABERIA aqui pelo critério (o 403 do gate é anterior à regra de
// negócio, então nada foi gravado) e está fora de propósito: a reconciliação
// recarrega o board, e board velho em aba aberta é uma das formas de chegar
// neste erro. Passar por ela conserta o que dá pra consertar — o board novo traz
// o estado real da trilha — em vez de deixar a tela mentindo. O custo é uma
// requisição num caminho de erro.
const NEVER_REACHED_CLAIM: DailyCheckinClaimErrorKind[] = [
  'unauthenticated',
  'invalid_user',
  'rate_limited',
];

// Store do "Check-in Diário" (Recompensas Diárias). A trilha é sequencial: o
// core manda o `status` de cada degrau e o front só decide qual deles é o botão
// de hoje — nada de recontar aqui o que o back já decidiu.
export const useDailyCheckinStore = defineStore('daily-checkin', () => {
  const authStore = useAuthStore();
  const { openLogin } = useAuth();
  const { userId } = useUserData();
  const playerProfileStore = usePlayerProfileStore();

  const isOpen = ref(false);

  const status = ref<DailyCheckinStatus>('idle');
  const board = ref<DailyCheckinBoard | null>(null);

  // Jogador que o core JÁ disse que está abaixo do nível mínimo do check-in
  // (`gamifications.daily_check_in.is_eligible: false` no /players/me). Mesmo
  // gate da roletinha, e desde o ENG-787 o ÚNICO: só o veredito FRESCO nega —
  // num "não sei" (perfil em trânsito, requisição falhada, core offline, mapa
  // quebrado) a trilha abre normal e a palavra final fica com o POST do resgate.
  // Recusar por falta de resposta tiraria o check-in de quem tem direito a ele.
  const isKnownIneligible = computed(() =>
    playerProfileStore.isKnownIneligibleFor(GAMIFICATION_SLUGS.dailyCheckIn),
  );

  const claiming = ref(false);
  const claimError = ref<string | null>(null);

  // Degrau aberto no popup (por position). null = popup fechado.
  const selectedPosition = ref<number | null>(null);

  const rewards = computed(() => board.value?.rewards ?? []);

  const hasActivePeriod = computed(
    () => !!board.value?.period && rewards.value.length > 0,
  );

  // Próximo degrau da fila = o primeiro ainda aberto. É a única leitura que o
  // front faz por cima do `status`: o contrato não marca "o de hoje" porque
  // isso é decisão de tela, não de regra.
  const nextPosition = computed(
    () =>
      rewards.value.find((reward) => reward.status === 'open')?.position ??
      null,
  );

  // Dias em atraso, direto do core (`missed_days`). É número INFORMATIVO, pra
  // texto — quem pinta cada degrau é o `status` dele. O front já calculou isso
  // por conta própria enquanto o endpoint não existia, e manter as duas contas
  // era garantir que um dia elas discordassem na cara do jogador.
  const missedCount = computed(() => board.value?.missedDays ?? 0);

  // O `missed` do core NÃO tem estado visual próprio (ver
  // DailyCheckinRewardState): ele cai no `pending` e é desenhado como um degrau
  // que ainda vai chegar. A distinção continua existindo no contrato — quem
  // decide se o resgate sai é o core, e nenhum `missed` chega a `claimable`
  // porque `nextPosition` só olha `open`.
  function stateOf(reward: DailyCheckinReward): DailyCheckinRewardState {
    if (reward.status === 'claimed') return 'claimed';

    // Só destaca o próximo degrau quando dá pra resgatar AGORA. Com o dia já
    // consumido ele volta a ser um degrau comum — amanhã não é diferente de
    // depois de amanhã.
    if (reward.position === nextPosition.value && board.value?.canClaimToday) {
      return 'claimable';
    }

    return 'pending';
  }

  // Em que degrau o jogador está HOJE. Com o dia livre é o próximo da fila;
  // com o dia já consumido é o último resgatado — o prêmio de hoje. Sem isso a
  // trilha fica sem âncora depois do resgate: o degrau volta a ser verde igual
  // aos anteriores e não dá pra ver onde você parou.
  const todayPosition = computed(() => {
    if (!hasActivePeriod.value) return null;
    if (board.value?.canClaimToday) return nextPosition.value;

    // A trilha é monotônica (resgata sempre o menor não resgatado), então o
    // último `claimed` é o de hoje.
    const lastClaimed = rewards.value.reduce<number | null>(
      (found, reward) =>
        reward.status === 'claimed' ? reward.position : found,
      null,
    );

    return lastClaimed;
  });

  // O que a tela consome: recompensa do contrato + estado derivado.
  const rewardViews = computed<DailyCheckinRewardView[]>(() =>
    rewards.value.map((reward) => ({
      ...reward,
      state: stateOf(reward),
      isToday: reward.position === todayPosition.value,
    })),
  );

  const selectedReward = computed(
    () =>
      rewardViews.value.find(
        (reward) => reward.position === selectedPosition.value,
      ) ?? null,
  );

  // `silent` é o resync de depois do resgate: não pisca o skeleton e, se falhar
  // ou voltar sem período, preserva a trilha que o jogador está vendo — um
  // resync que falha não pode apagar a tela no meio da comemoração.
  async function load({ silent = false } = {}) {
    // Autenticado mas sem perfil carregado ainda: sem `user_id` não dá pra
    // pedir o board. Cai na tela de erro (que tem "tentar novamente") em vez de
    // deixar o modal no skeleton pra sempre.
    if (!userId.value) {
      if (!silent) status.value = 'error';

      return;
    }

    // Jogador que o core já disse que não participa: responde sem pedir o board.
    // Vale também pro aquecimento silencioso do hub, e é aí que economiza — o
    // board é a requisição gorda (período + trilha + claims) que não muda nada
    // pra quem está fora da promoção.
    //
    // Diferente dos outros caminhos, isto grava estado mesmo em `silent`: não é
    // erro de rede que o aquecimento tem que engolir, é veredito do core.
    if (isKnownIneligible.value) {
      status.value = 'ineligible';

      return;
    }

    // Skeleton é pra quando não há NADA pra mostrar. Já tendo trilha na tela
    // (aquecida pelo hub, ou na virada da meia-noite com o modal aberto), a
    // busca acontece por baixo e os dados entram no lugar — trocar a trilha por
    // "Carregando..." seria piscar pra trás.
    if (!silent && status.value !== 'ready') status.value = 'loading';

    const [data, error] = await tryCatch(
      fetchDailyCheckinHandler({ userId: userId.value }),
    );

    // Falha de infra (rede, timeout, 5xx sem cache): 'error' com "Tentar
    // novamente" dentro do painel — igual à roletinha. O handler não mascara
    // mais isso como board vazio, então daqui pra baixo `data` é resposta do
    // core (ou o cache dele), nunca um sintético.
    if (error || !data) {
      if (!silent) status.value = 'error';

      return;
    }

    // Resync sem período não apaga a trilha que o jogador está vendo. Mas só há
    // o que preservar quando JÁ existe trilha: no aquecimento que o hub faz não
    // existe, e aí o vazio é a resposta — o 'unavailable' fica registrado pro
    // modal já nascer no aviso certo quando o jogador clicar.
    if (silent && !data.period && hasActivePeriod.value) return;

    board.value = data;

    // Daqui pra baixo resta UM motivo de não haver trilha pra abrir: não há
    // período no ar ('unavailable'). O 'ineligible' saiu do board no ENG-787 e
    // hoje tem uma origem só — o nível mínimo do `/players/me`, já resolvido no
    // early-return acima, antes mesmo de pedir o board.
    status.value = hasActivePeriod.value ? 'ready' : 'unavailable';
  }

  // Busca ignorando o cache de 5 min do handler. É o que se usa quando o board
  // sabidamente mudou (depois do resgate) ou quando a janela do cache mentiria
  // sobre o dia (na virada da meia-noite).
  async function refresh(options?: { silent?: boolean }) {
    clearDailyCheckinCache();
    await load(options);
  }

  // Resolve o estado ANTES de subir o sheet — igual à roletinha. O modal nasce
  // já na tela certa (trilha, aviso ou erro com "Tentar novamente"), em vez de
  // abrir no skeleton e TROCAR de painel na frente do jogador quando a resposta
  // chega: essa troca era um flick visível (painel desce, aviso sobe), e ainda
  // fazia o `after-leave` do <Transition> disparar como fechamento.
  //
  // Trilha já aquecida pelo hub ('ready'): abre na hora e revalida por baixo,
  // sem skeleton — o handler tem cache de 5 min, então isso raramente vai à
  // rede. Nos outros estados espera a resposta; o custo é o botão ficar sem
  // resposta pelo tempo do fetch, o mesmo custo que a roletinha já paga.
  async function open() {
    if (!authStore.isAuthenticated) {
      openLogin();

      return;
    }

    if (status.value === 'ready') {
      isOpen.value = true;
      load({ silent: true });

      return;
    }

    await load();

    isOpen.value = true;
  }

  function close() {
    isOpen.value = false;
    selectedPosition.value = null;
    claimError.value = null;
  }

  function openReward(position: number) {
    claimError.value = null;
    selectedPosition.value = position;
  }

  function closeReward() {
    selectedPosition.value = null;
    claimError.value = null;
  }

  // O resgate falhou — mas falhou mesmo? Quem sabe é o BOARD, não a resposta que
  // falhou. Recarrega ignorando o cache e devolve se o degrau que estava na vez
  // voltou como `claimed`: essa é a prova de que o core commitou.
  //
  // A pergunta é feita no degrau capturado ANTES da chamada porque ele era, por
  // construção, o primeiro `open` da trilha — qualquer `claimed` nele depois do
  // resync é informação nova, e só pode ter vindo deste resgate.
  //
  // Resync que não chega (rede fora) preserva a trilha antiga, então o degrau
  // segue `open` e a resposta é `false`: na dúvida, mostra o erro. É o lado
  // certo pra errar — o botão continua vivo e o core recusa a duplicata.
  async function reconcileClaim(position: number | null): Promise<boolean> {
    await refresh({ silent: true });

    if (position === null) return false;

    return rewards.value.some(
      (reward) => reward.position === position && reward.status === 'claimed',
    );
  }

  // Resgate. Não manda qual degrau: o servidor decide.
  async function claim() {
    if (claiming.value || !userId.value) return;
    if (!board.value?.canClaimToday) return;

    // Degrau que o core vai entregar (o menor ainda aberto). Guardado agora
    // porque é a chave da reconciliação lá embaixo, e depois da chamada a
    // trilha pode já ter mudado embaixo dele.
    const position = nextPosition.value;

    claiming.value = true;
    claimError.value = null;

    const [result, error] = await tryCatch(
      postDailyCheckinClaimHandler({ userId: userId.value }),
    );

    if (error || !result) {
      const kind = error instanceof DailyCheckinClaimError ? error.kind : 'api';

      // `claiming` FICA ligado durante a reconciliação: enquanto não se sabe o
      // desfecho, o botão não pode aceitar um segundo clique — era assim que se
      // chegava no "deu erro, cliquei de novo, deu certo".
      //
      // E a tela não fala NADA antes do veredito: anunciar a recusa aqui é o
      // bug, porque parte destes erros chega depois do prêmio já creditado.
      const recovered = NEVER_REACHED_CLAIM.includes(kind)
        ? false
        : await reconcileClaim(position);

      claiming.value = false;

      if (REPORTED_KINDS.includes(kind)) {
        Sentry.captureException(
          error instanceof Error ? error : new Error(String(error)),
          {
            tags: { source: 'daily-checkin.claim' },
            // `recovered` separa "o jogador perdeu o resgate" de "o jogador
            // recebeu, o core é que respondeu errado" — dois problemas
            // diferentes que sem esta flag chegam iguais na triagem.
            extra: { kind, recovered },
          },
        );
      }

      // O resgate valeu: o board já trouxe o degrau `claimed`, o dia fechado e a
      // hora do próximo resgate. Não há erro pra mostrar, e a virada
      // claimable → claimed acende a comemoração do popup igual ao caminho
      // felizardo — o jogador vê a mesma tela verde que veria se a resposta
      // tivesse chegado inteira.
      if (recovered) return;

      claimError.value = CLAIM_MESSAGES[kind];

      // Estes dois consomem o dia no core (a entrega incerta inclusive, mesmo
      // sem crédito confirmado) sem que o degrau fique `claimed` — então não
      // caem no `recovered`, e é aqui que o dia fecha. Na mão, porque o resync
      // da reconciliação pode ter falhado: sem isso o botão convida a tentar de
      // novo e o jogador acha que travou.
      if (
        board.value &&
        (kind === 'already_claimed_today' || kind === 'delivery_uncertain')
      ) {
        board.value.canClaimToday = false;
      }

      return;
    }

    claiming.value = false;

    // O resgate devolve só o prêmio, então o novo estado da trilha é aplicado
    // aqui na hora: a comemoração do popup depende da virada
    // claimable → claimed acontecer no clique, sem esperar rede.
    const target = board.value.rewards.find(
      (reward) => reward.position === result.position,
    );

    if (target) {
      Object.assign(target, result);
      target.status = 'claimed';
    }

    board.value.canClaimToday = false;
    board.value.claimedCount += 1;
    board.value.remainingCount = Math.max(0, board.value.remainingCount - 1);

    // E só então busca a verdade do core: contadores, degraus que viraram
    // `missed` e a hora do próximo resgate (que o claim não devolve). Sem
    // ignorar o cache, o resync leria a resposta de antes do resgate.
    await refresh({ silent: true });
  }

  // Limpa a TELA (o modal chama isto ao fechar), não o que se sabe do jogador: o
  // veredito de elegibilidade vive na store player-profile justamente por isso —
  // é de conta, não de modal aberto, e quem depende dele (o item do menu
  // horizontal) segue no ar depois de o check-in fechar.
  function reset() {
    // Fecha também: quem chama é o modal ao terminar de descer, e `isOpen`
    // precisa acompanhar — com ele preso em true, o próximo open() não muda
    // nada e o watch do modal não acorda.
    isOpen.value = false;
    status.value = 'idle';
    board.value = null;
    claiming.value = false;
    claimError.value = null;
    selectedPosition.value = null;
  }

  return {
    isOpen,
    status,
    board,
    rewardViews,
    hasActivePeriod,
    nextPosition,
    missedCount,
    claiming,
    claimError,
    selectedReward,
    open,
    close,
    openReward,
    closeReward,
    load,
    refresh,
    claim,
    reset,
  };
});
